import type { ErrorEvent } from '@sentry/nextjs'

import {
  DO_NOT_TRACK_COUNTRIES,
  TRACKING_PREFERENCE_KEY
} from 'utils/privacyPreferences'

const PII_HEADERS = new Set([
  'cookie',
  'set-cookie',
  'authorization',
  'x-forwarded-for',
  'x-real-ip',
  'x-bft-forwarded-for'
])

function headerValue(event: ErrorEvent, name: string): string | undefined {
  const headers = event.request?.headers
  if (!headers) return undefined

  const key = Object.keys(headers).find(
    (header) => header.toLowerCase() === name
  )
  return key ? headers[key] : undefined
}

function requestCookies(event: ErrorEvent): Record<string, string> {
  const raw = event.request?.cookies
  if (raw && !Array.isArray(raw)) return raw as Record<string, string>

  const header = headerValue(event, 'cookie')
  if (!header) return {}

  return Object.fromEntries(
    header.split('; ').flatMap((pair) => {
      const index = pair.indexOf('=')
      if (index === -1) return []
      const name = pair.slice(0, index)
      const value = pair.slice(index + 1)
      try {
        return [[name, decodeURIComponent(value)]]
      } catch {
        return [[name, value]]
      }
    })
  )
}

// sendDefaultPii is only read at init, so opt-outs mid-session are handled here.
// Mirrors bft-dashboards/src/common/utils/Sentry/pii.ts.
export function scrubEventPii(event: ErrorEvent): ErrorEvent {
  delete event.user

  if (event.request) {
    delete event.request.cookies
    delete event.request.data
    delete event.request.query_string

    const { headers } = event.request
    if (headers) {
      event.request.headers = Object.fromEntries(
        Object.entries(headers).filter(
          ([name]) => !PII_HEADERS.has(name.toLowerCase())
        )
      )
    }
  }

  return event
}

// Server/edge equivalent of isTrackingOptedOut(): no `navigator` here, so the
// preference comes off the event's own request cookies and the Sec-GPC header.
export function isRequestOptedOut(event: ErrorEvent): boolean {
  const cookies = requestCookies(event)

  const country = cookies['geo-country']
  if (country && DO_NOT_TRACK_COUNTRIES.includes(country)) return true

  const choice = cookies[TRACKING_PREFERENCE_KEY]
  if (choice === 'opted_out') return true
  if (choice === 'opted_in') return false

  return headerValue(event, 'sec-gpc') === '1'
}
